import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

import { collectDeltaDNA } from './api';

/**
 * Client telemetry relayed from the arcade games (bridge event ARCADE_UI_EVENT).
 *
 * The games send the client half of the VIP Arcade analytics contract, named as the
 * specification names it (`mini_game_opened`, `mini_game_entry_attempted`,
 * `mini_game_entry_failed`, `mini_game_error`, `mini_game_action`, `mini_game_result_shown`),
 * with `game_id`, `session_id` (when we gave the game one) and `membership_status` already
 * set. This module forwards them as they are, adding `platform` and `app_version`, and
 * never renames. Everything that carries a reward (entries taken, results, rewards,
 * tickets) is published by the backend, so a modified client cannot fake it.
 *
 * Sinks: deltaDNA through the existing collect endpoint; Helix through the tracker the
 * Helix integration registers with `setArcadeHelixTracker` (the SDK's `track`). Until one
 * is registered, Helix receives nothing from the arcade and the games are told so in
 * ARCADE_INIT (`analytics.helix: false`).
 */
export type ArcadeUiEventParams = Record<string, string | number | boolean>;

export interface ArcadeHelixBridge {
	/** `helix.track(name, properties)` of the app's Helix client. */
	track: (name: string, properties: Record<string, unknown>) => void;
	/**
	 * The client's current session id, so the games can stamp `session_id` on their events
	 * and send it to the server with every request. Return null when there is no session;
	 * the games then send none and nothing invents one.
	 */
	sessionId: () => string | null;
}

let helix: ArcadeHelixBridge | null = null;

/** Registered once by the Helix integration (app start / login). Pass null to unregister. */
export function setArcadeHelixTracker(bridge: ArcadeHelixBridge | null): void {
	helix = bridge;
}

export function isArcadeHelixEnabled(): boolean {
	return helix !== null;
}

/** The analytics session the games should join, or null when the app has none. */
export function arcadeAnalyticsSessionId(): string | null {
	try {
		return helix?.sessionId() ?? null;
	} catch {
		return null;
	}
}

export function arcadeAppVersion(): string {
	try {
		return DeviceInfo.getVersion();
	} catch {
		return '';
	}
}

export function trackArcadeUiEvent(name: string, params: ArcadeUiEventParams): void {
	const properties = {
		...params,
		platform: Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web',
		app_version: arcadeAppVersion(),
	};
	collectDeltaDNA(name, properties).catch(() => {
		// Telemetry must never disturb the game; the collect helper already logs failures.
	});
	if (helix) {
		try {
			helix.track(name, properties);
		} catch {
			// Same rule: a tracker failure costs analytics and nothing else.
		}
	}
}
